import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Alert, Stack } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, addDoc, doc, setDoc, Timestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth } from '../../firebase-config';
import theme from '../../theme';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE } from '../../lib/apiBase';
import { notifyNewUser } from '../../lib/adminNotify';
import { useT } from '../../i18n/LanguageContext';

const MAX_PROOF_SIZE = 5 * 1024 * 1024; // 5MB

const validatePassword = (pw) =>
  /[A-Z]/.test(pw) && /[!@#$%^&*(),.?":{}|<>]/.test(pw) && /\d/.test(pw);

// Book buyers request full access by submitting their Amazon order number.
// Creates a `submissions` doc; an admin verifies + grants access manually
// (Admin → Richieste / Utenti → Concedi Pro).
//
// Reachable while logged OUT: a book owner coming straight from the homepage
// has no account yet. The order-number form always shows first; if there's
// no signed-in user when they continue, a second inline step collects
// sign-in-or-create-account credentials on THIS SAME page (no navigation,
// no cross-tab session-storage hand-off) so the submission can be written
// with a real uid the moment auth resolves.
export default function RequestAccess() {
  const navigate = useNavigate();
  const t = useT();
  const { currentUser, isPro } = useAuth();
  const [name, setName] = useState(currentUser?.displayName || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [orderNumber, setOrderNumber] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const [authStep, setAuthStep] = useState(false);
  const [authMode, setAuthMode] = useState('signup'); // 'signup' | 'login'
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [orderProof, setOrderProof] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);

  const handleProofChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_PROOF_SIZE) {
      setError(t('billing.requestAccess.errorFileTooLarge'));
      e.target.value = '';
      return;
    }
    setError(null);
    setOrderProof(file);
    setProofPreview(URL.createObjectURL(file));
  };

  // Takes the Firebase user object (not just uid/email) — a fresh ID token
  // is required to call the notify endpoint, which rejects unauthenticated
  // requests (an open POST would otherwise let anyone spam the admin's
  // Telegram with arbitrary text).
  const writeSubmission = async (user) => {
    const uid = user.uid;
    const userEmail = user.email;
    let imageUrl = null;
    if (orderProof) {
      const storageRef = ref(getStorage(), `uploads/${uid}/order-proof-${Date.now()}-${orderProof.name}`);
      await uploadBytes(storageRef, orderProof);
      imageUrl = await getDownloadURL(storageRef);
    }
    await addDoc(collection(getFirestore(), 'submissions'), {
      userId: uid,
      email: userEmail,
      name: name.trim(),
      orderNumber: orderNumber.trim(),
      message: message.trim(),
      imageUrl,
      source: 'book',
      pending: true,
      createdAt: Timestamp.now(),
    });
    setDone(true);

    // Best-effort admin ping — never blocks or fails the submission itself.
    try {
      const idToken = await user.getIdToken();
      fetch(`${API_BASE}/notify/redeem-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ name: name.trim(), email: userEmail, orderNumber: orderNumber.trim(), message: message.trim() }),
      }).catch((e) => console.warn('Redeem admin notify failed (non-fatal):', e));
    } catch (e) {
      console.warn('Redeem admin notify skipped (no ID token):', e);
    }
  };

  const handleOrderContinue = async () => {
    if (!orderNumber.trim()) { setError(t('billing.requestAccess.errorOrderNumberRequired')); return; }
    if (!orderProof) { setError(t('billing.requestAccess.errorProofRequired')); return; }
    setError(null);
    if (currentUser) {
      setBusy(true);
      try {
        await writeSubmission(currentUser);
      } catch (e) {
        setError(e.message || t('billing.requestAccess.errorSubmitFallback'));
      }
      setBusy(false);
    } else {
      if (!email.trim()) { setError(t('billing.requestAccess.errorEmailRequired')); return; }
      setAuthStep(true);
    }
  };

  const handleAuthSubmit = async () => {
    setError(null);
    if (authMode === 'signup') {
      if (password !== confirmPassword) { setError(t('billing.requestAccess.errorPasswordMismatch')); return; }
      if (!validatePassword(password)) {
        setError(t('billing.requestAccess.errorPasswordRequirements'));
        return;
      }
    }
    setBusy(true);
    try {
      let user;
      if (authMode === 'signup') {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        user = cred.user;
        await sendEmailVerification(user);
        await setDoc(doc(getFirestore(), `users/${user.uid}`), {
          role: 'Free',
          email: user.email,
          displayName: name.trim() || user.email,
          createdAt: new Date(),
        });
        notifyNewUser(name.trim() || '');
      } else {
        const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
        user = cred.user;
      }
      await writeSubmission(user);
    } catch (e) {
      setError(e.message || t('billing.requestAccess.errorAuthFallback'));
    }
    setBusy(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: theme.home.paper, minHeight: '100vh' }}>
        <Navbar />
        <Box sx={{ maxWidth: 560, mx: 'auto', px: 3, py: { xs: 5, md: 8 } }}>
          <Typography variant="h4" sx={{ fontFamily: theme.home.display, fontWeight: 700, color: theme.home.ink, mb: 1.5 }}>
            {t('billing.requestAccess.title')}
          </Typography>

          {isPro ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              {t('billing.requestAccess.alreadyProAlert')}
            </Alert>
          ) : done ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              {t('billing.requestAccess.doneAlert')}
              {currentUser
                ? t('billing.requestAccess.doneAlertLoggedInSuffix')
                : t('billing.requestAccess.doneAlertLoggedOutSuffix')}
            </Alert>
          ) : authStep ? (
            <>
              <Typography sx={{ color: theme.home.inkMuted, mb: 3, lineHeight: 1.6 }}>
                {t('billing.requestAccess.authStepIntroPre')} {authMode === 'signup' ? t('billing.requestAccess.authStepIntroSignup') : t('billing.requestAccess.authStepIntroLogin')} {t('billing.requestAccess.authStepIntroPost')}
              </Typography>
              <Stack spacing={2}>
                <TextField label={t('billing.requestAccess.emailLabel')} value={email} onChange={(e) => setEmail(e.target.value)} fullWidth size="small" required />
                <TextField label={t('billing.requestAccess.passwordLabel')} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  fullWidth size="small" required />
                {authMode === 'signup' && (
                  <TextField label={t('billing.requestAccess.confirmPasswordLabel')} type="password" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)} fullWidth size="small" required />
                )}
                {error && <Alert severity="error">{error}</Alert>}
                <Button variant="contained" onClick={handleAuthSubmit} disabled={busy}
                  sx={{ borderRadius: 999, py: 1.2, bgcolor: theme.home.ink, '&:hover': { bgcolor: theme.home.inkHover } }}>
                  {busy ? t('billing.requestAccess.busyGeneric') : authMode === 'signup' ? t('billing.requestAccess.createAccountSubmit') : t('billing.requestAccess.loginSubmit')}
                </Button>
                <Button variant="text" onClick={() => { setAuthMode(authMode === 'signup' ? 'login' : 'signup'); setError(null); }}
                  sx={{ color: theme.home.inkMuted }}>
                  {authMode === 'signup' ? t('billing.requestAccess.switchToLogin') : t('billing.requestAccess.switchToSignup')}
                </Button>
                <Button variant="text" onClick={() => { setAuthStep(false); setError(null); }} sx={{ color: theme.home.inkMuted }}>
                  {t('billing.requestAccess.backButton')}
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography sx={{ color: theme.home.inkMuted, mb: 3, lineHeight: 1.6 }}>
                {t('billing.requestAccess.orderIntroPre')} <strong>{t('billing.requestAccess.orderIntroBold')}</strong> {t('billing.requestAccess.orderIntroMid')} <em>Sinergie Generative</em>.
                {' '}{t('billing.requestAccess.orderIntroPost')}
                {!currentUser && t('billing.requestAccess.orderIntroNoAccount')}
              </Typography>
              <Stack spacing={2}>
                <TextField label={t('billing.requestAccess.nameLabel')} value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" />
                <TextField label={t('billing.requestAccess.emailLabel')} value={email} onChange={(e) => setEmail(e.target.value)}
                  fullWidth size="small" disabled={!!currentUser} required={!currentUser} />
                <TextField label={t('billing.requestAccess.orderNumberLabel')} value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)}
                  fullWidth size="small" required placeholder={t('billing.requestAccess.orderNumberPlaceholder')} />
                <Button component="label" variant="outlined"
                  sx={{ borderRadius: 999, py: 1.1, borderColor: theme.home.paperDeep, color: theme.home.ink }}>
                  {orderProof ? t('billing.requestAccess.changeProofButton') : t('billing.requestAccess.uploadProofButton')}
                  <input hidden type="file" accept="image/*" onChange={handleProofChange} />
                </Button>
                {proofPreview && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box component="img" src={proofPreview} alt={t('billing.requestAccess.proofPreviewAlt')}
                      sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 2, border: `1px solid ${theme.home.paperDeep}` }} />
                    <Typography variant="body2" sx={{ color: theme.home.inkMuted }}>{orderProof.name}</Typography>
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: theme.home.inkFaint }}>
                  {t('billing.requestAccess.proofHint')}
                </Typography>
                <TextField label={t('billing.requestAccess.messageLabel')} value={message} onChange={(e) => setMessage(e.target.value)}
                  fullWidth size="small" multiline rows={2} />
                {error && <Alert severity="error">{error}</Alert>}
                <Button variant="contained" onClick={handleOrderContinue} disabled={busy}
                  sx={{ borderRadius: 999, py: 1.2, bgcolor: theme.home.ink, '&:hover': { bgcolor: theme.home.inkHover } }}>
                  {busy ? t('billing.requestAccess.sendingButton') : currentUser ? t('billing.requestAccess.sendButton') : t('billing.requestAccess.continueButton')}
                </Button>
                <Button variant="text" onClick={() => navigate(currentUser ? '/dashboard' : '/')} sx={{ color: theme.home.inkMuted }}>
                  {currentUser ? t('billing.requestAccess.backToDashboard') : t('billing.requestAccess.backToHome')}
                </Button>
              </Stack>
            </>
          )}
        </Box>
        <Footer />
      </Box>
    </ThemeProvider>
  );
}
